import {
  Button,
  Modal,
  TextField,
  FormLayout,
  Form,
  Text,
  LegacyStack,
  VerticalStack,
  Spinner,
  HorizontalStack,
  ButtonGroup,
  Checkbox,
  Box,
  Select,
} from '@shopify/polaris';
import { useState, useCallback, Suspense, useEffect, useMemo } from 'react';
import { getFilters } from '../../../services/productService';
import createResource from '../../../helpers/createResource';
import InputSelectorMultiple from '../Common/InputSelectorMultiple';
import { getDepositByIdStock, getDeposits } from '../../../services/depositsService';
import { useQuery } from 'react-query';

const filtersResource = createResource(() => getFilters().then(({ data }) => data.filters));

// Utilidades para convertir entre formato local y UTC
const toLocalDatetimeString = (isoString) => {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const localDatetimeToUTC = (localDatetimeString) => {
  const [datePart, timePart] = localDatetimeString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  // Crear fecha en hora local del navegador
  const localDate = new Date(year, month - 1, day, hours, minutes, 0);

  // Convertir a ISO string (UTC)
  const utcString = localDate.toISOString();
  return utcString;
};

// Función para obtener hora en huso horario específico
const getTimeInTimezone = (isoString, timezone) => {
  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timezone,
  });
  return formatter.format(date);
};

const Filters = ({ onSelectedFilters }) => {
  const filters = filtersResource.read();

  return (
    <VerticalStack gap={2}>
      {filters.map((filter) => (
        <InputSelectorMultiple
          key={filter.slug}
          label={filter.name}
          validOptions={filter.options.map((value) => ({
            label: value.toString(),
            value: typeof value === 'number' ? value.toString() : value,
          }))}
          //   selected={filter.values}
          onChange={(selected) => onSelectedFilters(filter.slug, selected)}
        />
      ))}
    </VerticalStack>
  );
};

const NumbersBox = ({ depositId, filters }) => {
  const [metadata, setMetadata] = useState({
    stockQty: 0,
    distinctProducts: 0,
    distinctVariants: 0,
  });
  const { data, isLoading, error, refetch } = useQuery(['productData', depositId, filters], () =>
    getDepositByIdStock(depositId, { page: 1, limit: 1, ...filters }),
  );

  useEffect(() => {
    refetch();
  }, [filters, refetch]);

  useEffect(() => {
    data && setMetadata(data.data.metadata);
  }, [data]);

  if (isLoading) return <Spinner accessibilityLabel="Small spinner example" size="small" />;

  if (error) return <div>Error: {error.message}</div>;

  const { stockQty, distinctProducts, distinctVariants } = metadata;

  return (
    <VerticalStack gap={2}>
      <LegacyStack distribution="fill">
        <span>
          <Text variant="headingXs" weight="semibold">
            Uds.
          </Text>
          <Text variant="heading3xl">{stockQty ?? 0}</Text>
        </span>
        <span>
          <Text variant="headingXs" weight="semibold">
            Serie-Color
          </Text>
          <Text variant="heading3xl">{distinctProducts}</Text>
        </span>
        <span>
          <Text variant="headingXs" weight="semibold">
            SKU
          </Text>
          <Text variant="heading3xl">{distinctVariants}</Text>
        </span>
      </LegacyStack>
      <Text variant="headingXs">* Resumen teórico basado en la situación actual de almacén</Text>
    </VerticalStack>
  );
};

export const NewInventoryModal = ({ title, onAddInventory, disable, suggestId }) => {
  const tomorrowAt8AM = new Date();
  tomorrowAt8AM.setDate(tomorrowAt8AM.getDate() + 1);
  tomorrowAt8AM.setHours(8, 0, 0, 0);
  // Convertir la hora local a UTC para almacenar
  const tomorrowAt8AMUTC = localDatetimeToUTC(toLocalDatetimeString(tomorrowAt8AM.toISOString()));

  const [selectedDepositId, setSelectedDepositId] = useState(suggestId);
  const [deposits, setDeposits] = useState([]);

  useEffect(() => {
    const fetchDeposits = async () => {
      try {
        const res = await getDeposits({ limit: 1000 });
        const p = res.data.data.map((d) => ({ label: d.name, value: parseInt(d.id) }));
        setDeposits(p);

        if (!suggestId) setSelectedDepositId(p.length > 0 ? p[0].value : '');
      } catch (e) {
        console.error('Error fetching deposits:', e);
      }
    };

    fetchDeposits();
  }, []);

  useEffect(() => {
    console.log('Fetched deposits:', deposits);
  }, [deposits]);

  const defaultData = {
    depositId: selectedDepositId || '',
    name: '',
    startDate: tomorrowAt8AMUTC,
    partialParams: {
      genders: [],
      categories: [],
      subcategories: [],
      years: [],
      seasons: [],
    },
    partialSeries: false,
  };

  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);

  const [dataForm, setDataForm] = useState(defaultData);
  const startDateES = useMemo(() => {
    const timeES = getTimeInTimezone(dataForm.startDate, 'Europe/Madrid');
    return timeES;
  }, [dataForm.startDate]);

  const startDateMX = useMemo(() => {
    const timeMX = getTimeInTimezone(dataForm.startDate, 'America/Mexico_City');
    return timeMX;
  }, [dataForm.startDate]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      switch (currentType) {
        case 'full':
          await onAddInventory(dataForm);
          setDataForm(defaultData);
          setActive(false);
          break;
        case 'partial':
          if (step === 1) {
            await onAddInventory(dataForm);
            setDataForm(defaultData);
            setActive(false);
          } else {
            setStep(step + 1);
          }
          break;
        case 'partialSeries':
          if (step === 1) {
            dataForm.partialSeries = true;
            dataForm.partialParams = {};
            await onAddInventory(dataForm);
            setDataForm(defaultData);
            setActive(false);
          } else {
            setStep(step + 1);
          }
          break;
        default:
          break;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const ConfigurePartialLayout = (
    <FormLayout.Group>
      <VerticalStack gap={2}>
        <Text>Seleccione los filtros que desea aplicar al inventario parcial</Text>
        <hr />

        <Suspense fallback={<div>Cargando filtros...</div>}>
          <Filters
            onSelectedFilters={(slug, value) => {
              const validSlug = slug === 'gender' ? 'genders' : slug;

              setDataForm({
                ...dataForm,
                partialParams: {
                  ...dataForm.partialParams,
                  [validSlug]: value,
                },
              });
            }}
          />
        </Suspense>
      </VerticalStack>
    </FormLayout.Group>
  );

  const ConfigurePartialSeriesLayout = (
    <FormLayout.Group>
      <VerticalStack gap={2}>
        <Text>
          Un inventario parcial de series sólo tendrá en cuenta las series-color completas. De tal modo que al cerrar un
          inventario de una serie-color, se borrarán los artículos por tallas no inventariados. El resto de series del
          almacén quedarán intactas.
        </Text>
      </VerticalStack>
    </FormLayout.Group>
  );

  const inventoryTypes = {
    full: {
      label: 'Inventario completo',
      nextStepLabel: 'Crear',
      nextStepAction: handleSubmit,
      nextStepDisabled: step === 0 && !dataForm.name,
      nextStepLayout: null,
    },
    partial: {
      label: 'Inventario parcial',
      nextStepLabel: 'Siguiente',
      nextStepAction: () => setStep(step + 1),
      nextStepDisabled: step === 0 && !dataForm.name,
      nextStepLayout: ConfigurePartialLayout,
    },
    partialSeries: {
      label: 'Inventario parcial por series',
      nextStepLabel: 'Siguiente',
      nextStepAction: () => setStep(step + 1),
      nextStepDisabled: step === 0 && !dataForm.name,
      nextStepLayout: ConfigurePartialSeriesLayout,
    },
  };

  const [currentType, setCurrentType] = useState(Object.keys(inventoryTypes)[0]);

  const handleChange = useCallback(() => {
    console.log('[Modal] Toggle - Active:', !active);
    setActive(!active);
  }, [active]);
  const activator = (
    <Button onClick={handleChange} disabled={disable}>
      {title || 'Nuevo Inventario'}
    </Button>
  );
  const handleInventoryTypeChange = (type) => {
    setCurrentType(type);
    setDataForm({
      ...dataForm,
      partialParams: defaultData.partialParams,
      partialSeries: currentType === 'partialSeries',
    });
  };

  return (
    <>
      {activator}
      {active && (
        <Modal
          // activator={activator}
          open={active}
          onClose={handleChange}
          title={title || 'Nuevo Inventario'}
          primaryAction={{
            content: step === 1 ? 'Crear' : inventoryTypes[currentType].nextStepLabel,
            onAction: step === 1 ? handleSubmit : inventoryTypes[currentType].nextStepAction,
            disabled: inventoryTypes[currentType].nextStepDisabled,
          }}
          secondaryActions={
            step === 0
              ? null
              : {
                  content: 'Anterior',
                  onAction: () => setStep(step - 1),
                }
          }
        >
          <Modal.Section>
            {selectedDepositId && <NumbersBox depositId={selectedDepositId} filters={dataForm.partialParams} />}
          </Modal.Section>
          <Modal.Section>
            <Form onSubmit={handleSubmit}>
              <FormLayout>
                {step === 0 && (
                  <>
                    <FormLayout.Group>
                      {deposits.length > 0 && (
                        <>
                          <Text variant="headingMd" as="h4">
                            Depósito
                          </Text>
                          <Box border="divider" borderRadius="base" minHeight=".5rem" />
                          <Select
                            options={deposits}
                            value={selectedDepositId}
                            onChange={(value) => {
                              console.log('Selected deposit ID:', value);
                              setDataForm({ ...dataForm, depositId: parseInt(value) });
                              setSelectedDepositId(parseInt(value));
                            }}
                          />
                        </>
                      )}
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <TextField
                        label="Nombre"
                        type="name"
                        value={dataForm.name}
                        autoComplete="off"
                        onChange={(e) => setDataForm({ ...dataForm, name: e })}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <div className="flex flex-col-reverse md:grid md:grid-cols-[2fr_1fr] gap-4 ">
                        <VerticalStack gap={1}>
                          <TextField
                            label="Fecha de inicio"
                            type="datetime-local"
                            value={dataForm.startDate ? toLocalDatetimeString(dataForm.startDate) : ''}
                            onChange={(e) => {
                              console.log('[Input datetime-local] valor cambiado:', e);
                              setDataForm({ ...dataForm, startDate: localDatetimeToUTC(e) });
                            }}
                            disabled={dataForm.startDateManually}
                          />
                          <Text variant="bodySm">Hora en México: {dataForm.startDate ? startDateMX : ''}</Text>
                          <Text variant="bodySm">Hora en España: {dataForm.startDate ? startDateES : ''}</Text>
                        </VerticalStack>
                        <div className="mt-4">
                          <Checkbox
                            label="Iniciar manualmente"
                            type="checkbox"
                            checked={dataForm.startDateManually}
                            onChange={(e) => {
                              console.log('[Input checkbox] valor cambiado:', e);
                              setDataForm({
                                ...dataForm,
                                startDateManually: e,
                                startDate: e ? null : tomorrowAt8AMUTC,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <HorizontalStack align="center">
                        <ButtonGroup segmented>
                          {Object.keys(inventoryTypes).map((t) => (
                            <Button
                              key={t}
                              primary={currentType === t}
                              onClick={() => !(currentType === t) && handleInventoryTypeChange(t)}
                            >
                              {inventoryTypes[t].label}
                            </Button>
                          ))}
                        </ButtonGroup>
                      </HorizontalStack>
                    </FormLayout.Group>
                  </>
                )}
                {step === 1 && inventoryTypes[currentType].nextStepLayout}
              </FormLayout>
            </Form>
          </Modal.Section>
        </Modal>
      )}
    </>
  );
};
